import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatFarmCalendarDate } from "@/lib/farmTime";
import { Button } from "@/components/ui/button";
import { LogOut, CalendarDays, Sparkles } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import EmployeeBottomNav from "@/components/employee/EmployeeBottomNav";
import { AJEmblem } from "@/components/brand/AJFarmLogo";
import BrandFooter from "@/components/brand/BrandFooter";
import { useTranslation } from "react-i18next";
import EmployeeSyncCenter from "@/components/employee/EmployeeSyncCenter";

export default function EmployeeShell() {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const nav = useNavigate();
    const today = formatFarmCalendarDate(new Date(), {
        weekday: "short",
        month: "short",
        day: "numeric",
    });

    const doLogout = async () => {
        await logout();
        nav("/login");
    };

    return (
        <div className="min-h-screen bg-background text-foreground pb-24 lg:pb-8 aj-app-shell">
            <Toaster richColors position="top-center" />
            <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
                <div className="mx-auto w-full max-w-[1200px] px-4 py-3 sm:px-6 lg:px-8 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <AJEmblem className="w-11 h-11 shrink-0" />
                        <div className="min-w-0">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{t("field.title")}</div>
                            <div className="font-semibold truncate">{user?.name || t("profile.employee")}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
                            <CalendarDays className="w-4 h-4" />
                            <span>{today}</span>
                        </div>
                        {process.env.REACT_APP_AI_ENABLED !== "0" ? (
                            <Button variant="ghost" size="sm" onClick={() => nav("/field/ai-katulong")} data-testid="employee-ai-katulong-btn">
                                <Sparkles className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">{t("nav.assistant")}</span>
                            </Button>
                        ) : null}
                        <Button variant="ghost" size="sm" onClick={doLogout} data-testid="employee-logout-btn">
                            <LogOut className="w-4 h-4 mr-2" />{t("common.logout")}
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto w-full max-w-[1200px] px-4 py-4 sm:px-6 lg:px-8 aj-app-content">
                <EmployeeSyncCenter />
                <Outlet />
            </main>

            <BrandFooter />
            <EmployeeBottomNav />
        </div>
    );
}
